import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  AvailableAction,
  EmrState,
  ExtensionPage,
  ExtensionStatus,
  PatientDetailsEmr,
} from '../storeTypes';
import { resetAction } from '../actions/resetAction';

const initialState: EmrState = {
  extensionPage: { pageName: 'noContentAvailable' },
  extensionStatus: { state: 'unknown' },
};

const emrSlice = createSlice({
  name: 'emrSlice',
  initialState,
  extraReducers: (builder) => builder.addCase(resetAction, () => initialState),
  reducers: {
    setUrlInActiveTabEmrAction: (
      draftState: EmrState,
      action: PayloadAction<{ url: string | undefined }>,
    ) => {
      const { url } = action.payload;
      // Reset per-URL EMR state on a url change, but PRESERVE extensionStatus:
      // the handshake is a property of the browser/extension, not of the active
      // EMR tab's URL. Blanking it here would drop a good 'connected' status
      // (only useEmrExtension writes it, and it doesn't re-ping on url change),
      // making the submit button falsely grey. (This legacy path fires only for
      // the removed side-panel `currentUrl` message, but keep it safe.)
      const preservedStatus = draftState.extensionStatus;
      Object.assign(draftState, initialState);
      draftState.extensionStatus = preservedStatus;
      draftState.urlInActiveTab = url;
    },
    setPatientDetailsEmrAction: (
      draftState: EmrState,
      action: PayloadAction<{
        patientDetails: PatientDetailsEmr | undefined;
      }>,
    ) => {
      const { patientDetails } = action.payload;
      draftState.patientDetails = patientDetails;
    },
    setAvailableActionsEmrAction: (
      draftState: EmrState,
      action: PayloadAction<{
        availableActions: AvailableAction[] | undefined;
      }>,
    ) => {
      const { availableActions } = action.payload;
      draftState.availableActions = availableActions;
    },
    setIsExtensionModeEmrAction: (
      draftState: EmrState,
      action: PayloadAction<{
        isExtensionMode: boolean;
      }>,
    ) => {
      const { isExtensionMode } = action.payload;
      draftState.isExtensionMode = isExtensionMode;
    },
    setExtensionPageAction: (
      draftState: EmrState,
      action: PayloadAction<{
        extensionPage: ExtensionPage;
      }>,
    ) => {
      const { extensionPage } = action.payload;
      draftState.extensionPage = extensionPage;
    },
    clearExtensionPageAction: (draftState: EmrState) => {
      draftState.extensionPage = { pageName: 'noContentAvailable' };
    },
    setExtensionStatusEmrAction: (
      draftState: EmrState,
      action: PayloadAction<{ extensionStatus: ExtensionStatus }>,
    ) => {
      draftState.extensionStatus = action.payload.extensionStatus;
    },
  },
});

export const {
  setUrlInActiveTabEmrAction,
  setPatientDetailsEmrAction,
  setAvailableActionsEmrAction,
  setIsExtensionModeEmrAction,
  setExtensionPageAction,
  clearExtensionPageAction,
  setExtensionStatusEmrAction,
} = emrSlice.actions;

export default emrSlice;
